import { gql } from '@apollo/client';
import { Button } from 'components/buttons/button';
import { StandardInput } from 'components/forms/input';
import {
  Booksellers,
  useUpdateBooksellerMutation,
  useRemoveBooksellerMutation,
  GetAllBooksellersDocument,
} from 'lib/graphql-generated';
import { useInput } from 'lib/hooks/use-input';
import { useState } from 'react';

const UPDATE_BOOKSELLER = gql`
  mutation UpdateBookseller($data: booksellers_set_input, $id: Int!) {
    update_booksellers_by_pk(pk_columns: { id: $id }, _set: $data) {
      id
      avatar
      name
      video_url
    }
  }
`;

const DELETE_BOOKSELLER = gql`
  mutation RemoveBookseller($id: Int!) {
    delete_booksellers_by_pk(id: $id) {
      id
    }
  }
`;

interface Props {
  bookseller: Partial<Booksellers>;
}

export const BooksellerCard = ({ bookseller }: Props) => {
  const name = useInput<string>(bookseller.name);
  const avatar = useInput<string>(bookseller.avatar);
  const videoUrl = useInput<string>(bookseller.video_url);
  const about = useInput<string>(bookseller.about);
  const [isEditing, setEditing] = useState<boolean>(false);
  const [save, { data, loading, error }] = useUpdateBooksellerMutation();
  const [
    remove,
    { data: deleteData, loading: deleteLoading, error: deleteError },
  ] = useRemoveBooksellerMutation();

  const canSave =
    (name.isModified ||
      avatar.isModified ||
      videoUrl.isModified ||
      about.isModified) &&
    !loading;

  const handleSave = (e) => {
    e.preventDefault();
    save({
      variables: {
        id: bookseller.id,
        data: {
          avatar: avatar.value,
          name: name.value,
          video_url: videoUrl.value,
          about: about.value,
        },
      },
    }).then(() => setEditing(false));
  };

  const handleRemove = (e) => {
    e.preventDefault();
    if (
      confirm(
        `Are you sure you want to delete ${bookseller.name}?\nAll posts associated to this bookseller will now be associated to Lighthouse Bookshop`
      )
    ) {
      remove({
        variables: {
          id: bookseller.id,
        },
        refetchQueries: ['GetAllBooksellers'],
      });
    }
  };

  return (
    <>
      <li className='col-span-1 flex flex-col text-center bg-white rounded-lg shadow divide-y divide-gray-200 pt-8'>
        {isEditing ? (
          <form
            className='pb-8 px-4 flex flex-col space-y-4'
            name={`${bookseller.id}_booksellerform`}
            id={`${bookseller.id}_booksellerform`}>
            <label
              htmlFor='name'
              className='w-full text-sm font-medium text-gray-700 text-left'>
              Name
              <input
                {...name.bind}
                type='text'
                name='name'
                id='name'
                autoComplete='off'
                autoCorrect='off'
                className='mt-1 focus:ring-green-default focus:border-green-default block w-full shadow-sm sm:text-sm border-gray-300 rounded-md'
              />
            </label>
            <label
              htmlFor='avatar'
              className='w-full text-sm font-medium text-gray-700 text-left'>
              Avatar URL
              <input
                {...avatar.bind}
                type='url'
                name='avatar'
                id='avatar'
                autoComplete='off'
                autoCorrect='off'
                className='mt-1 focus:ring-green-default focus:border-green-default block w-full shadow-sm sm:text-sm border-gray-300 rounded-md'
              />
            </label>
            <label
              htmlFor='videoUrl'
              className='w-full text-sm font-medium text-gray-700 text-left'>
              Video URL
              <input
                {...videoUrl.bind}
                type='url'
                name='videoUrl'
                id='videoUrl'
                autoComplete='off'
                autoCorrect='off'
                className='mt-1 focus:ring-green-default focus:border-green-default block w-full shadow-sm sm:text-sm border-gray-300 rounded-md'
              />
            </label>
            <StandardInput
              label='About'
              id='about'
              name='about'
              bind={{ ...about.bind }}
              type='text'
            />
          </form>
        ) : (
          <div className='flex-1 flex flex-col pb-8'>
            <img
              className='w-32 h-32 flex-shrink-0 m-auto  bg-yellow-default shadow-inner rounded-full'
              src={bookseller.avatar}
              alt={`${bookseller.name}'s avatar`}
            />
            <h3 className='mt-6 text-gray-900 text-sm font-medium'>
              {bookseller.name}
            </h3>
            <dl className='mt-1 flex-grow flex flex-col justify-between'>
              <dt className='sr-only'>Video URL</dt>
              <dd className='text-green-default italic text-sm'>
                <a href={bookseller.video_url}>View Video</a>
              </dd>
            </dl>
          </div>
        )}
        <section className='flex flex-row py-4 px-4 mt-auto'>
          {isEditing && (
            <>
              <Button
                type='submit'
                form={`${bookseller.id}_booksellerform`}
                formTarget={`${bookseller.id}_booksellerform`}
                buttonType='primary'
                buttonSize='sm'
                className='mr-4'
                onClick={handleSave}
                disabled={!canSave}>
                Save
              </Button>
              <button
                type='button'
                className='mr-auto text-sm text-red-default'
                onClick={handleRemove}>
                Delete
              </button>
            </>
          )}
          <Button
            type='button'
            buttonType='ghost'
            buttonSize='sm'
            className={`${isEditing ? 'ml-auto' : 'mx-auto'} mt-auto'`}
            onClick={(e) => {
              e.preventDefault();
              setEditing(!isEditing);
            }}>
            {isEditing ? 'Cancel' : 'Edit'}
          </Button>
        </section>
      </li>
    </>
  );
};
